import type { AppContext } from "./context";
import type { ScreenEntry } from "../core/types";
import { movePip, clamp, clampCrop, type CropRect } from "../compose/layout";
import { AppError } from "../core/errors";
import { el, formatClock, on, toast } from "./dom";
import { mountPill } from "./pill";

export function mountRecordUi(ctx: AppContext): void {
  const { session } = ctx;
  const canvas = el<HTMLCanvasElement>("stage");
  const stageEmpty = el<HTMLDivElement>("stage-empty");
  const stageBadge = el<HTMLDivElement>("stage-badge");
  const stageFrame = el<HTMLDivElement>("stage-frame");
  const fullscreenButton = el<HTMLButtonElement>("stage-fullscreen");
  const countdown = el<HTMLDivElement>("countdown");
  const countdownNum = el<HTMLSpanElement>("countdown-num");
  const screenList = el<HTMLUListElement>("screen-list");
  const addScreenButton = el<HTMLButtonElement>("add-screen");
  const emptyAddScreen = el<HTMLButtonElement>("empty-add-screen");
  const cameraToggle = el<HTMLInputElement>("camera-toggle");
  const cameraHelp = el<HTMLElement>("camera-help");
  const pipShapeRow = el<HTMLElement>("pip-shape-row");
  const pipSizeRow = el<HTMLElement>("pip-size-row");
  const micToggle = el<HTMLInputElement>("mic-toggle");
  const systemAudioToggle = el<HTMLInputElement>("system-audio-toggle");
  const pipShape = el<HTMLSelectElement>("pip-shape");
  const pipSize = el<HTMLInputElement>("pip-size");
  const pipSizeOut = el<HTMLOutputElement>("pip-size-out");
  const mirrorToggle = el<HTMLInputElement>("mirror-toggle");
  const qualitySelect = el<HTMLSelectElement>("quality");
  const fpsSelect = el<HTMLSelectElement>("fps");
  const formatLine = el<HTMLParagraphElement>("format-line");
  const recordButton = el<HTMLButtonElement>("record-btn");
  const recordLabel = el<HTMLSpanElement>("record-btn-label");
  const status = el<HTMLSpanElement>("rec-status");
  const micMeter = el<HTMLSpanElement>("mic-meter");
  const systemMeter = el<HTMLSpanElement>("system-meter");
  const hint = el<HTMLDivElement>("stage-hint");

  mountPill(session);

  // ---------------------------------------------------------- fullscreen
  function syncFullscreenButton(): void {
    const active = document.fullscreenElement === stageFrame;
    fullscreenButton.setAttribute("aria-label", active ? "Exit full screen" : "Full screen preview");
    fullscreenButton.setAttribute("aria-pressed", String(active));
    fullscreenButton.title = active ? "Exit full screen (Esc)" : "Full screen (F)";
  }

  function toggleFullscreen(): void {
    if (document.fullscreenElement === stageFrame) {
      void document.exitFullscreen().catch(() => {});
      return;
    }
    void stageFrame.requestFullscreen().catch(() => {
      toast("Full screen was refused by the browser.", "error");
    });
  }

  on(fullscreenButton, "click", toggleFullscreen);
  document.addEventListener("fullscreenchange", syncFullscreenButton);
  on(window, "keydown", (event) => {
    if (event.key !== "f" && event.key !== "F") return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target?.isContentEditable) return;
    if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
    if (el<HTMLElement>("view-record").hidden) return;
    event.preventDefault();
    toggleFullscreen();
  });
  syncFullscreenButton();

  // ------------------------------------------------------------- screens
  function renderScreens(): void {
    screenList.replaceChildren();
    const entries = session.screenEntries;
    if (entries.length === 0) {
      const empty = document.createElement("li");
      empty.className = "source-empty";
      empty.textContent = "No screens yet.";
      screenList.append(empty);
    }
    for (const entry of entries) {
      screenList.append(screenRow(entry));
    }
    stageEmpty.hidden = entries.length > 0 || session.hasCamera;
    // The camera fills the frame when it is the only source, so the corner
    // controls have nothing to act on.
    const cameraOnly = session.hasCamera && entries.length === 0;
    pipShapeRow.hidden = cameraOnly;
    pipSizeRow.hidden = cameraOnly;
    cameraHelp.textContent = !session.hasCamera
      ? "With a screen shared it sits in the corner; on its own it fills the frame."
      : cameraOnly
        ? "No screen shared — the camera fills the frame."
        : "Camera sits in the corner. Remove every screen to record the camera on its own.";
    fullscreenButton.hidden = stageEmpty.hidden === false;
    stageBadge.hidden = entries.length === 0;
    // Not "arming": that phase is the countdown, and this button cancels it.
    recordButton.disabled =
      session.phase === "finalizing" ||
      (entries.length === 0 && !session.hasCamera);
  }

  function screenRow(entry: ScreenEntry): HTMLLIElement {
    const li = document.createElement("li");
    li.className = "source-item";

    const thumb = document.createElement("div");
    thumb.className = "source-thumb";
    const paintThumb = (attempt = 0) => {
      const url = session.stage.thumbnailFor(entry.id);
      if (url) thumb.style.backgroundImage = `url(${url})`;
      else if (attempt < 6) window.setTimeout(() => paintThumb(attempt + 1), 400);
    };
    window.setTimeout(() => paintThumb(), 200);

    const meta = document.createElement("div");
    meta.className = "source-meta";
    const name = document.createElement("div");
    name.className = "source-name";
    name.textContent = entry.label;
    const sub = document.createElement("div");
    sub.className = "source-sub";
    const track = entry.stream.getVideoTracks()[0];
    const settings = track?.getSettings();
    const dims = settings?.width && settings?.height ? `${settings.width}×${settings.height}` : "live";
    const crop = session.stage.getCrop(entry.id);
    const area = crop ? `area ${Math.round(crop.w * 100)}×${Math.round(crop.h * 100)}%` : "whole screen";
    sub.textContent = [dims, area, entry.hasSystemAudio ? "with system audio" : null]
      .filter(Boolean)
      .join(" · ");
    meta.append(name, sub);

    const areaButton = document.createElement("button");
    areaButton.className = "btn small";
    areaButton.type = "button";
    areaButton.textContent = crop ? "Whole" : "Area";
    areaButton.title = crop
      ? "Record the whole screen again"
      : session.isRecording
        ? "Change the recorded area now (the take keeps its current frame shape)"
        : "Record only part of this screen";
    on(areaButton, "click", () => {
      if (crop) {
        session.setScreenCrop(entry.id, null);
        return;
      }
      void openAreaSelector(entry);
    });

    const remove = document.createElement("button");
    remove.className = "btn small danger";
    remove.type = "button";
    remove.textContent = "Remove";
    remove.title = session.isRecording
      ? "Stop sharing this screen (the take keeps recording)"
      : "Remove this screen";
    remove.setAttribute("aria-label", `Remove ${entry.label}`);
    on(remove, "click", () => {
      session.removeScreen(entry.id);
    });

    li.append(thumb, meta, areaButton, remove);
    return li;
  }

  /**
   * Picks the part of a screen that gets recorded. The browser can only hand
   * back a whole screen, a window or a tab, so a region is a crop of the
   * captured frame.
   *
   * The overlay shows a LIVE clone of the same track, not a cached composite:
   * what you drag over is exactly what is behind the dialog, at full size.
   */
  async function openAreaSelector(entry: ScreenEntry): Promise<void> {
    const track = entry.stream.getVideoTracks()[0];
    if (!track) {
      toast("This screen has no video track.", "error");
      return;
    }

    const overlay = document.createElement("div");
    overlay.className = "area-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-label", "Choose the area to record");

    const panel = document.createElement("div");
    panel.className = "area-panel";

    const title = document.createElement("h2");
    title.textContent = "Choose the area to record";

    const stageCanvas = document.createElement("canvas");
    stageCanvas.className = "area-canvas";
    stageCanvas.width = 960;
    stageCanvas.height = 540;
    const ctx = stageCanvas.getContext("2d");

    const readout = document.createElement("p");
    readout.className = "fine area-readout";
    readout.textContent = "Starting the live view…";

    const actions = document.createElement("div");
    actions.className = "dialog-actions";
    const useButton = document.createElement("button");
    useButton.className = "btn";
    useButton.type = "button";
    useButton.textContent = "Use area";
    useButton.disabled = true;
    const wholeButton = document.createElement("button");
    wholeButton.className = "btn ghost";
    wholeButton.type = "button";
    wholeButton.textContent = "Whole screen";
    const cancelButton = document.createElement("button");
    cancelButton.className = "btn ghost";
    cancelButton.type = "button";
    cancelButton.textContent = "Cancel";
    actions.append(wholeButton, cancelButton, useButton);

    panel.append(title, stageCanvas, readout, actions);
    overlay.append(panel);

    let latest: VideoFrame | null = null;
    let sourceSize: { width: number; height: number } | null = null;
    let selection: { x: number; y: number; w: number; h: number } | null = null;
    let start: { x: number; y: number } | null = null;
    let stopped = false;
    let raf = 0;
    let reader: ReadableStreamDefaultReader<VideoFrame> | null = null;
    let clone: MediaStreamTrack | null = null;

    try {
      clone = track.clone();
      const processor = new MediaStreamTrackProcessor({ track: clone });
      reader = processor.readable.getReader();
      void (async () => {
        while (!stopped) {
          let result: ReadableStreamReadResult<VideoFrame>;
          try {
            result = await reader!.read();
          } catch {
            break;
          }
          if (result.done) break;
          if (latest) latest.close();
          latest = result.value;
        }
      })();
    } catch (error) {
      console.error("area picker: could not clone the track", error);
      toast("The live view is unavailable; using the last frame.", "info");
    }

    function paint(): void {
      const frame = latest;
      if (!frame || !ctx) return;
      if (!sourceSize) {
        sourceSize = { width: frame.displayWidth, height: frame.displayHeight };
        const scale = Math.min(
          (window.innerWidth * 0.86) / sourceSize.width,
          (window.innerHeight * 0.66) / sourceSize.height,
          1,
        );
        stageCanvas.width = Math.max(1, Math.round(sourceSize.width * scale));
        stageCanvas.height = Math.max(1, Math.round(sourceSize.height * scale));
        readout.textContent = `${sourceSize.width} × ${sourceSize.height} px. Drag over the part you want recorded.`;
      }
      ctx.drawImage(frame, 0, 0, stageCanvas.width, stageCanvas.height);
      ctx.fillStyle = "rgba(13, 11, 9, 0.55)";
      ctx.fillRect(0, 0, stageCanvas.width, stageCanvas.height);
      if (selection && selection.w > 0 && selection.h > 0) {
        const { x, y, w, h } = selection;
        ctx.drawImage(frame, 0, 0, stageCanvas.width, stageCanvas.height);
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, stageCanvas.width, stageCanvas.height);
        ctx.rect(x, y, w, h);
        ctx.clip("evenodd");
        ctx.fillStyle = "rgba(13, 11, 9, 0.55)";
        ctx.fillRect(0, 0, stageCanvas.width, stageCanvas.height);
        ctx.restore();
        ctx.strokeStyle = "#e2564c";
        ctx.lineWidth = 2;
        ctx.strokeRect(x + 1, y + 1, Math.max(1, w - 2), Math.max(1, h - 2));
      }
    }

    function loop(): void {
      if (stopped) return;
      paint();
      raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);

    function point(event: PointerEvent): { x: number; y: number } {
      const rect = stageCanvas.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * stageCanvas.width;
      const y = ((event.clientY - rect.top) / rect.height) * stageCanvas.height;
      return { x: clamp(x, 0, stageCanvas.width), y: clamp(y, 0, stageCanvas.height) };
    }

    function close(): void {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      window.removeEventListener("keydown", onKey);
      void reader?.cancel().catch(() => {});
      reader = null;
      if (latest) {
        latest.close();
        latest = null;
      }
      if (clone) {
        clone.stop();
        clone = null;
      }
      overlay.remove();
    }

    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") close();
      if (event.key === "Enter" && selection) applySelection();
    }

    function applySelection(): void {
      if (!selection || !sourceSize) return;
      if (selection.w < 8 || selection.h < 8) return;
      const crop: CropRect = clampCrop({
        x: selection.x / stageCanvas.width,
        y: selection.y / stageCanvas.height,
        w: selection.w / stageCanvas.width,
        h: selection.h / stageCanvas.height,
      });
      session.setScreenCrop(entry.id, crop);
      close();
    }

    on(stageCanvas, "pointerdown", (event) => {
      const p = point(event);
      start = p;
      selection = { x: p.x, y: p.y, w: 0, h: 0 };
      stageCanvas.setPointerCapture(event.pointerId);
      event.preventDefault();
    });

    on(stageCanvas, "pointermove", (event) => {
      if (!start) return;
      const p = point(event);
      selection = {
        x: Math.min(start.x, p.x),
        y: Math.min(start.y, p.y),
        w: Math.abs(p.x - start.x),
        h: Math.abs(p.y - start.y),
      };
      if (sourceSize) {
        const pxW = Math.round((selection.w / stageCanvas.width) * sourceSize.width);
        const pxH = Math.round((selection.h / stageCanvas.height) * sourceSize.height);
        readout.textContent = `${pxW} × ${pxH} px of ${sourceSize.width} × ${sourceSize.height}`;
      }
      useButton.disabled = selection.w < 8 || selection.h < 8;
    });

    on(stageCanvas, "pointerup", (event) => {
      if (!start) return;
      start = null;
      stageCanvas.releasePointerCapture(event.pointerId);
    });

    on(useButton, "click", applySelection);
    on(wholeButton, "click", () => {
      session.setScreenCrop(entry.id, null);
      close();
    });
    on(cancelButton, "click", close);
    window.addEventListener("keydown", onKey);

    document.body.append(overlay);
  }

  async function addScreen(): Promise<void> {
    addScreenButton.disabled = true;
    try {
      const entry = await session.addScreen();
      if (entry) renderScreens();
    } catch (error) {
      const appError = error instanceof AppError ? error : null;
      toast(appError?.message ?? String(error), "error");
    } finally {
      addScreenButton.disabled = false;
      recordButton.disabled = session.isRecording
        ? false
        : session.screenEntries.length === 0 && !session.hasCamera;
    }
  }

  on(addScreenButton, "click", () => void addScreen());
  on(emptyAddScreen, "click", () => void addScreen());

  // ------------------------------------------------------------- toggles
  on(cameraToggle, "change", async () => {
    const on_ = cameraToggle.checked;
    cameraToggle.disabled = true;
    try {
      await session.setCamera(on_);
      ctx.settings.cameraOn = on_;
      await ctx.persist();
    } catch (error) {
      cameraToggle.checked = false;
      ctx.settings.cameraOn = false;
      toast(error instanceof AppError ? error.message : "Camera unavailable.", "error");
    } finally {
      cameraToggle.disabled = false;
      renderScreens();
    }
  });

  on(micToggle, "change", async () => {
    const on_ = micToggle.checked;
    micToggle.disabled = true;
    try {
      await session.setMic(on_);
      ctx.settings.micOn = on_;
      await ctx.persist();
    } catch (error) {
      micToggle.checked = false;
      ctx.settings.micOn = false;
      toast(error instanceof AppError ? error.message : "Microphone unavailable.", "error");
    } finally {
      micToggle.disabled = false;
    }
  });

  on(systemAudioToggle, "change", () => {
    session.setSystemAudio(systemAudioToggle.checked);
    ctx.settings.systemAudioOn = systemAudioToggle.checked;
    void ctx.persist();
  });

  // --------------------------------------------------------------- pip
  on(pipShape, "change", () => {
    const shape = pipShape.value as "circle" | "rounded" | "square";
    const pip = { ...session.stage.getPip(), shape };
    session.setPip(pip);
    ctx.settings.pip = pip;
    void ctx.persist();
  });

  on(pipSize, "input", () => {
    const size = clamp(Number(pipSize.value) / 100, 0.06, 0.75);
    pipSizeOut.textContent = `${pipSize.value}%`;
    const pip = { ...session.stage.getPip(), size };
    session.setPip(pip);
    ctx.settings.pip = pip;
    void ctx.persist();
  });

  on(mirrorToggle, "change", () => {
    const pip = { ...session.stage.getPip(), mirrored: mirrorToggle.checked };
    session.setPip(pip);
    ctx.settings.pip = pip;
    void ctx.persist();
  });

  on(qualitySelect, "change", () => {
    const height = Number(qualitySelect.value) as 720 | 1080 | 1440;
    const fps = Number(fpsSelect.value) as 30 | 60;
    session.setQuality({ height, fps });
    ctx.settings.quality = { height, fps };
    void ctx.persist();
  });

  on(fpsSelect, "change", () => {
    const height = Number(qualitySelect.value) as 720 | 1080 | 1440;
    const fps = Number(fpsSelect.value) as 30 | 60;
    session.setQuality({ height, fps });
    ctx.settings.quality = { height, fps };
    void ctx.persist();
  });

  // ------------------------------------------------------ pip dragging
  let dragging = false;
  let lastX = 0;
  let lastY = 0;

  function canvasPoint(event: MouseEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((event.clientY - rect.top) / rect.height) * canvas.height;
    return { x, y };
  }

  on(canvas, "pointerdown", (event) => {
    const point = canvasPoint(event);
    if (!session.stage.hitPip(point.x, point.y)) return;
    dragging = true;
    lastX = point.x;
    lastY = point.y;
    canvas.setPointerCapture(event.pointerId);
    event.preventDefault();
  });

  on(canvas, "pointermove", (event) => {
    const point = canvasPoint(event);
    if (!dragging) {
      canvas.style.cursor = session.stage.hitPip(point.x, point.y) ? "grab" : "default";
      return;
    }
    const pip = movePip(
      session.stage.getPip(),
      { dx: point.x - lastX, dy: point.y - lastY },
      canvas.width,
      canvas.height,
    );
    lastX = point.x;
    lastY = point.y;
    session.setPip(pip);
    ctx.settings.pip = pip;
    hint.textContent = "Camera moved.";
  });

  on(canvas, "pointerup", (event) => {
    if (!dragging) return;
    dragging = false;
    canvas.releasePointerCapture(event.pointerId);
    void ctx.persist();
  });

  on(canvas, "wheel", (event) => {
    const point = canvasPoint(event);
    if (!session.stage.hitPip(point.x, point.y)) return;
    event.preventDefault();
    const pip = session.stage.getPip();
    const next = clamp(pip.size + (event.deltaY > 0 ? -0.01 : 0.01), 0.06, 0.75);
    const updated = { ...pip, size: next };
    session.setPip(updated);
    ctx.settings.pip = updated;
    pipSize.value = String(Math.round(next * 100));
    pipSizeOut.textContent = `${Math.round(next * 100)}%`;
    void ctx.persist();
  }, { passive: false });

  // ------------------------------------------------------------ record
  on(recordButton, "click", async () => {
    if (session.isRecording) {
      recordButton.disabled = true;
      recordLabel.textContent = "Saving…";
      try {
        const take = await session.stop();
        if (!take) recordLabel.textContent = "Record";
      } finally {
        recordButton.disabled = false;
        syncRecordButton();
      }
      return;
    }
    if (session.phase === "arming") {
      await session.finalize("user");
      return;
    }
    if (session.phase === "finalizing") return;
    recordButton.disabled = true;
    try {
      await session.start();
    } catch (error) {
      const message =
        error instanceof AppError ? error.message : "Recording could not start.";
      toast(message, "error");
    } finally {
      syncRecordButton();
    }
  });

  function syncRecordButton(): void {
    const phase = session.phase;
    recordButton.classList.toggle("stopping", phase === "recording" || phase === "paused");
    if (phase === "arming") {
      recordButton.disabled = false;
      recordButton.classList.add("stopping");
      recordLabel.textContent = "Cancel";
    } else if (phase === "recording" || phase === "paused") {
      recordButton.disabled = false;
      recordLabel.textContent = "Stop";
    } else if (phase === "finalizing") {
      recordButton.disabled = true;
      recordLabel.textContent = "Saving…";
    } else {
      recordButton.disabled = session.screenEntries.length === 0 && !session.hasCamera;
      recordLabel.textContent = "Record";
    }
    status.textContent =
      phase === "recording"
        ? `Recording ${formatClock(session.elapsedMs)}`
        : phase === "paused"
          ? "Paused"
          : phase === "arming"
            ? "Starting"
            : phase === "finalizing"
              ? "Saving"
              : phase === "saved"
                ? "Saved"
                : phase === "failed"
                  ? "Not saved"
                  : "Ready";
    status.classList.toggle("live", phase === "recording" || phase === "paused");
    document.title = phase === "recording" ? `● ${formatClock(session.elapsedMs)} — Record` : "Record";
    countdown.hidden = phase !== "arming";
    if (phase !== "arming") hint.textContent = session.hasCamera ? "Drag the camera to move it. Scroll over it to resize." : "";
    formatLine.textContent = `Format: ${session.formatLabel}`;
  }

  session.events.on("phase", () => {
    syncRecordButton();
    renderScreens();
  });
  session.events.on("sources", () => {
    renderScreens();
    syncRecordButton();
  });
  session.events.on("tick", (value) => {
    if (session.phase === "arming") countdownNum.textContent = String(value);
    else if (session.isRecording) {
      status.textContent = session.phase === "paused" ? "Paused" : `Recording ${formatClock(session.elapsedMs)}`;
      document.title = `● ${formatClock(session.elapsedMs)} — Record`;
    }
  });
  session.events.on("levels", (levels) => {
    micMeter.style.width = `${Math.round(levels.mic * 100)}%`;
    systemMeter.style.width = `${Math.round(levels.system * 100)}%`;
  });
  session.events.on("saved", (take) => {
    toast(`Saved ${take.fileName}`, "ok");
    void ctx.refreshLibrary();
    // Embed mode has one view: the portal shows the result, not this app.
    if (document.body.dataset.embed !== "1") ctx.showTab("library");
  });
  session.events.on("failed", (error) => {
    toast(error.message, "error");
  });
  // Every session notice used to be dropped: a take saved to the recovery
  // journal, a stalled encoder, a dead microphone all looked like nothing.
  session.events.on("notice", (notice) => {
    if (!notice.message) return;
    toast(notice.message, notice.kind === "error" ? "error" : "info");
  });

  // initial state from settings
  cameraToggle.checked = ctx.settings.cameraOn;
  micToggle.checked = ctx.settings.micOn;
  systemAudioToggle.checked = ctx.settings.systemAudioOn;
  pipShape.value = ctx.settings.pip.shape;
  pipSize.value = String(Math.round(ctx.settings.pip.size * 100));
  pipSizeOut.textContent = `${Math.round(ctx.settings.pip.size * 100)}%`;
  mirrorToggle.checked = ctx.settings.pip.mirrored;
  qualitySelect.value = String(ctx.settings.quality.height);
  fpsSelect.value = String(ctx.settings.quality.fps);
  renderScreens();
  syncRecordButton();

  void (async () => {
    if (ctx.settings.cameraOn) {
      try {
        await session.setCamera(true);
      } catch {
        cameraToggle.checked = false;
        ctx.settings.cameraOn = false;
      }
    }
    if (ctx.settings.micOn) {
      try {
        await session.setMic(true);
      } catch {
        micToggle.checked = false;
        ctx.settings.micOn = false;
      }
    }
    renderScreens();
    syncRecordButton();
  })();
}
